import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './AboutUsPhilosophyForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_POINTS_PER_ITEM = 5;

const FIXED_ITEMS = [
  { key: 'motto', label: 'School Motto' },
  { key: 'philosophy', label: 'Philosophy' },
  { key: 'logo', label: 'Logo Philosophy' }
];

export default function AboutUsPhilosophyForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    items: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [modalItemData, setModalItemData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      console.log('[AboutUsPhilosophyForm] Fetching media map...');
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) {
        console.warn('[AboutUsPhilosophyForm] Media fetch failed:', response.status);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        
        console.log('[AboutUsPhilosophyForm] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsPhilosophyForm] Media fetch error:', err.message);
    }
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[AboutUsPhilosophyForm] Loading philosophy section...');
      
      let philosophySection = null;
      
      // Try admin endpoint
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          philosophySection = result.data.find(s => s.section_type === 'about-us-philosophy-shell');
        }
      }
      
      // Fallback to public endpoint
      if (!philosophySection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            philosophySection = result.data.find(s => s.section_type === 'about-us-philosophy-shell');
          }
        }
      }
      
      if (!philosophySection) {
        throw new Error('Philosophy section not found');
      }

      setSectionId(philosophySection.id);
      console.log('[AboutUsPhilosophyForm] Found section:', philosophySection.id);

      // Load pending changes
      let pendingFieldsMap = {};
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${philosophySection.id}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
          const { pending_changes } = pendingResult.data;
          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                // Handle item operations: items.0.update, items.1.update, etc
                const itemMatch = change.field_id.match(/^items\.(\d+)\./);
                if (itemMatch) {
                  const itemIdx = parseInt(itemMatch[1]);
                  pendingFieldsMap[`item_${itemIdx}`] = true;
                  console.log('[AboutUsPhilosophyForm] Item', itemIdx, 'has pending approval');
                } else {
                  // Regular field
                  pendingFieldsMap[change.field_id] = true;
                }
              }
            });
          }
        }
      }

      if (philosophySection.content_published) {
        let published = philosophySection.content_published;
        if (typeof published === 'string') {
          published = JSON.parse(published);
        }

        const publishedEn = published.en || {};
        const initialData = {
          items: Array.isArray(publishedEn.items) ? publishedEn.items : []
        };

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingFieldsMap);
        console.log('[AboutUsPhilosophyForm] ✓ Section loaded with', initialData.items.length, 'items');
      }
    } catch (err) {
      console.error('[AboutUsPhilosophyForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (field, value) => {
    setModalItemData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePointChange = (pointIndex, field, value) => {
    setModalItemData(prev => ({
      ...prev,
      points: prev.points.map((point, pIdx) =>
        pIdx === pointIndex ? { ...point, [field]: value } : point
      )
    }));
  };

  const handleAddPoint = () => {
    if (!modalItemData) return;

    const currentPoints = modalItemData.points || [];
    if (currentPoints.length >= MAX_POINTS_PER_ITEM) {
      toast.warning(`Maximum ${MAX_POINTS_PER_ITEM} points allowed`);
      return;
    }

    setModalItemData(prev => ({
      ...prev,
      points: [...(prev.points || []), { title: '', description: '' }]
    }));
  };

  const handleDeletePoint = (pointIndex) => {
    setModalItemData(prev => ({
      ...prev,
      points: prev.points.filter((_, pIdx) => pIdx !== pointIndex)
    }));
  };

  const handleImageUpload = async (e) => {
    if (!editingItemIndex && editingItemIndex !== 0) return;
    
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('[AboutUsPhilosophyForm] Image upload started:', { filename: file.name, itemIndex: editingItemIndex });

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [editingItemIndex]: true }));
    setImageProgress(prev => ({ ...prev, [editingItemIndex]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [editingItemIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[AboutUsPhilosophyForm] Upload completed, status:', xhr.status);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              console.log('[AboutUsPhilosophyForm] ✓ Image uploaded, media ID:', mediaId);
              if (mediaId) {
                handleItemChange('image', mediaId);
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              }
            }
          } catch (parseErr) {
            console.error('[AboutUsPhilosophyForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [editingItemIndex]: false }));
      });

      xhr.addEventListener('error', () => {
        console.error('[AboutUsPhilosophyForm] XHR error');
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [editingItemIndex]: false }));
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-philosophy-shell`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[AboutUsPhilosophyForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [editingItemIndex]: false }));
    }
  };

  const handleSubmitItem = async (itemIndex, itemDataToSubmit = null) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Use provided itemDataToSubmit (from modal) or fallback to formData
    const item = itemDataToSubmit || formData.items[itemIndex];
    const itemKey = `item_${itemIndex}`;

    if (!item || !item.title) {
      toast.warning('Please fill in required fields');
      return;
    }

    // Validate points if they exist
    if (item.points && item.points.length > 0) {
      const hasInvalidPoint = item.points.some(p => !p.title || !p.description);
      if (hasInvalidPoint) {
        toast.warning('All points must have title and description');
        return;
      }
    }

    setSubmitting(prev => ({ ...prev, [itemKey]: true }));

    try {
      console.log('[AboutUsPhilosophyForm] Submitting item', itemIndex, {
        label: FIXED_ITEMS[itemIndex].label,
        title: item.title,
        pointsCount: item.points?.length || 0
      });

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `items.${itemIndex}.update`,
            field_name: `Update ${FIXED_ITEMS[itemIndex].label}`,
            old_value: originalData.items[itemIndex] || {},
            new_value: item,
            locale: 'en'
          })
        }
      );

      console.log('[AboutUsPhilosophyForm] Response status:', response.status);
      const result = await response.json();
      console.log('[AboutUsPhilosophyForm] Response result:', result);

      if (result.success || response.ok) {
        toast.success(`${FIXED_ITEMS[itemIndex].label} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [itemKey]: true }));
        // Update both formData and originalData with the submitted item
        setFormData(prev => ({
          ...prev,
          items: prev.items.map((c, i) => i === itemIndex ? item : c)
        }));
        setOriginalData(prev => ({
          ...prev,
          items: prev.items.map((c, i) => i === itemIndex ? item : c)
        }));
      } else {
        if (response.status === 409) {
          toast.error('This item already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[AboutUsPhilosophyForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="hero-form-wrapper">
        <div className="loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading School Philosophy...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />

      <div className="hero-form-header">
        <h1 className="hero-form-title">School Philosophy</h1>
        <p className="hero-form-description">Edit philosophy items individually for approval</p>
      </div>

      {/* Items Section - Fixed 3 items (Motto, Philosophy, Logo) */}
      <div className="form-section">
        <h2 className="form-section-title">Philosophy Items</h2>

        {formData.items.length === 0 ? (
          <div className="items-empty-state">
            <div className="empty-icon">📭</div>
            <h4>No items available</h4>
            <p>Check the database configuration</p>
          </div>
        ) : (
          <div className="items-list">
            {FIXED_ITEMS.map((fixedItem, idx) => {
              const item = formData.items[idx];
              const itemKey = `item_${idx}`;
              const isPending = pendingFields[itemKey];
              
              if (!item) {
                return (
                  <div key={idx} className="items-list-item">
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      Item data not found: {fixedItem.label}
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={idx} className={`items-list-item ${isPending ? 'item-pending' : ''}`}>
                  <div className="items-list-content">
                    <h4 className="items-list-title">{fixedItem.label}</h4>
                    <p className="items-list-summary">{item.title || 'Click to edit'}</p>
                  </div>
                  
                  <div className="items-list-actions">
                    {isPending && (
                      <span className="items-list-status">Pending</span>
                    )}
                    <button
                      onClick={() => {
                        setEditingItemIndex(idx);
                        setModalItemData({ ...item });
                      }}
                      disabled={isPending}
                      className="items-list-btn items-list-btn-edit"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Item Modal */}
      {editingItemIndex !== null && modalItemData && (
        <div className="edit-modal-overlay" onClick={() => {
          setEditingItemIndex(null);
          setModalItemData(null);
        }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-new">
              <h3>Edit {FIXED_ITEMS[editingItemIndex].label}</h3>
              <button 
                className="modal-close-btn" 
                onClick={() => {
                  setEditingItemIndex(null);
                  setModalItemData(null);
                }}
              >×</button>
            </div>

            <div className="modal-body">
              {/* Title Field */}
              <div className="modal-field-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={modalItemData?.title || ''}
                  onChange={(e) => handleItemChange('title', e.target.value)}
                  placeholder={`${FIXED_ITEMS[editingItemIndex].label} title...`}
                  className="modal-input"
                  autoFocus
                />
              </div>

              {/* Short Title */}
              <div className="modal-field-group">
                <label>Short Title</label>
                <input
                  type="text"
                  value={modalItemData?.shortTitle || ''}
                  onChange={(e) => handleItemChange('shortTitle', e.target.value)}
                  placeholder="Short title (optional)..."
                  className="modal-input"
                />
              </div>

              {/* Description */}
              {modalItemData?.description !== null && (
                <div className="modal-field-group">
                  <label>Description</label>
                  <textarea
                    value={modalItemData?.description || ''}
                    onChange={(e) => handleItemChange('description', e.target.value)}
                    placeholder="Description..."
                    className="modal-textarea"
                    rows="3"
                  />
                </div>
              )}

              {/* Quote */}
              {modalItemData?.quote !== null && (
                <div className="modal-field-group">
                  <label>Quote</label>
                  <textarea
                    value={modalItemData?.quote || ''}
                    onChange={(e) => handleItemChange('quote', e.target.value)}
                    placeholder="Quote..."
                    className="modal-textarea"
                    rows="2"
                  />
                </div>
              )}

              {/* Image Section */}
              <div className="modal-field-group">
                <label>Item Image</label>
                {modalItemData?.image && (
                  <div className="modal-image-section">
                    <div className="modal-image-preview">
                      {mediaMap[modalItemData.image] ? (
                        <img 
                          src={mediaMap[modalItemData.image]} 
                          alt={FIXED_ITEMS[editingItemIndex].label}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="modal-image-placeholder" style={{display: mediaMap[modalItemData.image] ? 'none' : 'flex'}}>
                        <span>ID: {modalItemData.image} (URL not found)</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal-upload-area">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading[editingItemIndex] || pendingFields[`item_${editingItemIndex}`]}
                    className="modal-file-input"
                    id={`item-image-${editingItemIndex}`}
                  />
                  <label
                    htmlFor={`item-image-${editingItemIndex}`}
                    className="modal-upload-label"
                  >
                    <span className="upload-icon-new">📤</span>
                    <span>
                      {imageUploading[editingItemIndex] ? `Uploading ${imageProgress[editingItemIndex]}%...` : 'Click to upload image'}
                    </span>
                  </label>
                </div>
                <span className="hint">Max 5MB • Recommended: 400×300px</span>
              </div>

              {/* Points Section */}
              <div className="modal-field-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label>Points {modalItemData?.points && modalItemData.points.length > 0 ? `(${modalItemData.points.length}/${MAX_POINTS_PER_ITEM})` : ''}</label>
                  <button
                    type="button"
                    onClick={handleAddPoint}
                    disabled={pendingFields[`item_${editingItemIndex}`] || (modalItemData?.points && modalItemData.points.length >= MAX_POINTS_PER_ITEM)}
                    style={{
                      padding: '6px 12px',
                      background: '#4299e1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    + Add Point
                  </button>
                </div>

                {modalItemData?.points && modalItemData.points.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {modalItemData.points.map((point, pIdx) => (
                      <div key={pIdx} style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#718096' }}>Point {pIdx + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleDeletePoint(pIdx)}
                            disabled={pendingFields[`item_${editingItemIndex}`]}
                            style={{
                              background: '#fee2e2',
                              color: '#991b1b',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Delete
                          </button>
                        </div>

                        <input
                          type="text"
                          value={point.title || ''}
                          onChange={(e) => handlePointChange(pIdx, 'title', e.target.value)}
                          placeholder="Point title..."
                          className="modal-input"
                          style={{ marginBottom: '8px' }}
                        />

                        <textarea
                          value={point.description || ''}
                          onChange={(e) => handlePointChange(pIdx, 'description', e.target.value)}
                          placeholder="Point description..."
                          className="modal-textarea"
                          rows="2"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#a0aec0', background: '#f7fafc', borderRadius: '8px' }}>
                    No points added yet. Click "Add Point" to create one.
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-btn-cancel" 
                onClick={() => {
                  setEditingItemIndex(null);
                  setModalItemData(null);
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleSubmitItem(editingItemIndex, modalItemData);
                  setEditingItemIndex(null);
                  setModalItemData(null);
                }}
                disabled={submitting[`item_${editingItemIndex}`] || pendingFields[`item_${editingItemIndex}`] || !modalItemData?.title}
                className="modal-btn-submit"
              >
                {submitting[`item_${editingItemIndex}`] ? 'Submitting...' : 'Save & Submit For Approval'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
